import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import BriefingLoadingState from './briefing/BriefingLoadingState';

// When public login is disabled (private-beta production), unauthenticated
// requests to protected routes bounce to /sooncome instead of /login so
// nobody sees a login form by accident. Single source of truth read by
// both the auth funnel and the protected-route fallback.
const LOGIN_DISABLED = process.env.REACT_APP_LOGIN_DISABLED === 'true';
const UNAUTH_DEST = LOGIN_DISABLED ? '/sooncome' : '/login';

export default function ProtectedRoute({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  const navState = useMemo(() => ({ from: location.pathname }), [location.pathname]);

  if (loading) {
    // Path-aware loading state — the Briefing's cinematic moment
    // gets the BrandedLoading shell so the ProtectedRoute flash
    // doesn't break the visual continuity of the reveal. All other
    // routes get the existing minimal spinner.
    if (location.pathname.includes('/briefing')) {
      return <BriefingLoadingState />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to={UNAUTH_DEST} state={navState} replace />;
  }

  // Gate all protected routes behind onboarding UNLESS we're already on the wizard
  if (requireOnboarding && user.onboarding_complete === false && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}
